import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import {
  organizationAdministrationKeys,
  useGetFinancialYear,
  useUpdateFinancialYear,
} from "../../../hooks/query/organization-administration";
import PageHeader from "../../../components/text/PageHeader";
import {
  DEFAULT_FINANCIAL_YEAR,
  formatPeriod,
  maxDayOfMonth,
  periodFor,
} from "../../../utils/financialYear";

// Lime focus for raw Chakra controls (project convention — never the blue ring).
const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** A day + month pair. The day list follows the month, and a day the month lacks is pulled back into it. */
function DayMonthPicker({ label, day, month, onChange, t }) {
  const days = useMemo(() => Array.from({ length: maxDayOfMonth(month) }, (_, i) => i + 1), [month]);
  return (
    <Box>
      <Text fontSize="body.xs" color="gray.500" mb={1}>{label}</Text>
      <HStack>
        <Select
          size="sm"
          w="80px"
          aria-label={`${label} — ${t("day")}`}
          value={day}
          onChange={(e) => onChange({ day: Number(e.target.value), month })}
          {...LIME_FOCUS}
        >
          {days.map((d) => <option key={d} value={d}>{d}</option>)}
        </Select>
        <Select
          size="sm"
          w="160px"
          aria-label={`${label} — ${t("month")}`}
          value={month}
          onChange={(e) => {
            const nextMonth = Number(e.target.value);
            onChange({ day: Math.min(day, maxDayOfMonth(nextMonth)), month: nextMonth });
          }}
          {...LIME_FOCUS}
        >
          {MONTHS.map((m) => <option key={m} value={m}>{t(`months.${m}`)}</option>)}
        </Select>
      </HStack>
    </Box>
  );
}

/**
 * ESG-4018: the organisation's financial year. A reporting year is the financial year that ENDS in it, so
 * with 1 Apr – 31 Mar "2026" means 1 Apr 2025 – 31 Mar 2026. Foundation Data extracts only figures for a
 * workstream's reporting period, which is why the preview states it in exactly those words.
 */
function FinancialYearSettings() {
  const { t } = useTranslation("translation", { keyPrefix: "financialYear" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const thisYear = new Date().getFullYear();

  const { data: config } = useGetFinancialYear();
  const [start, setStart] = useState({ day: DEFAULT_FINANCIAL_YEAR.startDay, month: DEFAULT_FINANCIAL_YEAR.startMonth });
  const [end, setEnd] = useState({ day: DEFAULT_FINANCIAL_YEAR.endDay, month: DEFAULT_FINANCIAL_YEAR.endMonth });
  const [overrides, setOverrides] = useState([]);
  const [newYear, setNewYear] = useState(String(thisYear));

  useEffect(() => {
    if (config) {
      setStart({ day: config.startDay, month: config.startMonth });
      setEnd({ day: config.endDay, month: config.endMonth });
      setOverrides(config.overrides ?? []);
    }
  }, [config]);

  const draft = useMemo(
    () => ({ startDay: start.day, startMonth: start.month, endDay: end.day, endMonth: end.month, overrides }),
    [start, end, overrides]
  );
  // The default only — an override for the preview year would make the preview stop describing the setting.
  const preview = useMemo(() => periodFor({ ...draft, overrides: [] }, thisYear), [draft, thisYear]);

  const { mutate: updateFinancialYear, isLoading } = useUpdateFinancialYear({
    onSuccess: () => {
      queryClient.invalidateQueries(organizationAdministrationKeys.financialYear());
      showSuccessToast(toast, t("saved"));
    },
    // The server sends every problem at once as a JSON list in parameters.message.
    onError: (err) => {
      const raw = err.response?.data?.parameters?.message;
      let message = raw ?? err.message;
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) message = parsed.join(" · ");
      } catch {
        // not JSON — show as sent
      }
      showErrorToast(toast, message);
    },
  });

  const addOverride = () => {
    const year = Number(newYear);
    if (!Number.isFinite(year) || overrides.some((o) => o.year === year)) return;
    // Seeded from the default, so the person edits the one or two days that differ rather than typing both.
    const seed = periodFor({ ...draft, overrides: [] }, year);
    setOverrides((prev) => [...prev, { year, startDate: seed.start, endDate: seed.end }].sort((a, b) => a.year - b.year));
  };

  const updateOverride = (year, field, value) =>
    setOverrides((prev) => prev.map((o) => (o.year === year ? { ...o, [field]: value } : o)));

  const removeOverride = (year) => setOverrides((prev) => prev.filter((o) => o.year !== year));

  const save = () => updateFinancialYear(draft);

  return (
    <Page>
      <Box maxW="720px">
        <PageHeader title={t("title")} subTitle={t("description")} />

        <Stack spacing={8} mt={6}>
          <Box>
            <Text fontSize="body.m" fontWeight="600" color="gray.700" mb={1}>{t("defaultLabel")}</Text>
            <Text fontSize="body.xs" color="gray.500" mb={3}>{t("defaultHint")}</Text>
            <HStack spacing={6} align="flex-end" flexWrap="wrap">
              <DayMonthPicker label={t("starts")} day={start.day} month={start.month} onChange={setStart} t={t} />
              <DayMonthPicker label={t("ends")} day={end.day} month={end.month} onChange={setEnd} t={t} />
            </HStack>
            <Text fontSize="body.s" color="gray.700" mt={3}>
              {t("preview", { year: thisYear, period: formatPeriod(preview) })}
            </Text>
          </Box>

          <Box>
            <Text fontSize="body.m" fontWeight="600" color="gray.700" mb={1}>{t("overridesLabel")}</Text>
            <Text fontSize="body.xs" color="gray.500" mb={3}>{t("overridesHint")}</Text>

            <HStack mb={3} align="flex-end">
              <Box>
                <Text fontSize="body.xs" color="gray.500" mb={1}>{t("reportingYear")}</Text>
                <Input
                  size="sm"
                  w="120px"
                  type="number"
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  {...LIME_FOCUS}
                />
              </Box>
              <Button size="sm" variant="secondary" leftIcon={<Plus size={14} />} onClick={addOverride}>
                {t("add")}
              </Button>
            </HStack>

            <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
              <Table size="sm" variant="unstyled">
                <Thead>
                  <Tr bg="gray.50">
                    <Th py={2} px={4} fontSize="13px" color="gray.600" textTransform="none">{t("reportingYear")}</Th>
                    <Th py={2} px={4} fontSize="13px" color="gray.600" textTransform="none">{t("startDate")}</Th>
                    <Th py={2} px={4} fontSize="13px" color="gray.600" textTransform="none">{t("endDate")}</Th>
                    <Th py={2} px={4} width="48px" />
                  </Tr>
                </Thead>
                <Tbody>
                  {overrides.length === 0 ? (
                    <Tr>
                      <Td colSpan={4} py={4} px={4} textAlign="center" fontSize="14px" color="gray.400">
                        {t("noOverrides")}
                      </Td>
                    </Tr>
                  ) : (
                    overrides.map((o) => (
                      <Tr key={o.year} borderTop="1px solid" borderColor="gray.100">
                        <Td py={2} px={4} fontSize="14px" color="gray.700">{o.year}</Td>
                        <Td py={2} px={4}>
                          <Input
                            size="sm"
                            type="date"
                            aria-label={`${o.year} ${t("startDate")}`}
                            value={o.startDate ?? ""}
                            onChange={(e) => updateOverride(o.year, "startDate", e.target.value)}
                            {...LIME_FOCUS}
                          />
                        </Td>
                        <Td py={2} px={4}>
                          <Input
                            size="sm"
                            type="date"
                            aria-label={`${o.year} ${t("endDate")}`}
                            value={o.endDate ?? ""}
                            onChange={(e) => updateOverride(o.year, "endDate", e.target.value)}
                            {...LIME_FOCUS}
                          />
                        </Td>
                        <Td py={2} px={4} textAlign="right">
                          <IconButton
                            aria-label={t("removeOverride", { year: o.year })}
                            icon={<Trash size={14} />}
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            _hover={{ color: "red.500", bg: "red.50" }}
                            onClick={() => removeOverride(o.year)}
                          />
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>

          <Flex justify="flex-end">
            <Button variant="primary" onClick={save} isLoading={isLoading}>{t("save")}</Button>
          </Flex>
        </Stack>
      </Box>
    </Page>
  );
}

export default WithAuthorization(FinancialYearSettings);
